import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { toast } from 'sonner';
import { Plus, Save, Loader2, Barcode } from 'lucide-react';

export function NewCustomerDialog({
  open, onOpenChange,
  customerName, setCustomerName,
  creating, onCreateCustomer,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add New Customer</DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <Label>Customer Name *</Label>
          <Input
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="Enter customer name"
            className="mt-2"
            data-testid="input-new-customer-name"
          />
          <p className="text-sm text-muted-foreground mt-2">
            You can add full details later from the Customers page.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onCreateCustomer} disabled={creating} data-testid="btn-create-customer">
            {creating ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" /> Create Customer</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SupplierCodeDialog({
  open, onOpenChange,
  customerData, lines, lineIndex,
  products, cobrandings,
  supplierCode, setSupplierCode,
  saving, onSave, onUpdateLine,
}) {
  const currentLine = lines[lineIndex];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Barcode className="w-5 h-5 text-blue-600" />
            Create New Customer Code
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="bg-muted/50 p-3 rounded-lg">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Customer:</span>
              <span className="font-medium">{customerData?.name || '\u2014'}</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Product *</Label>
            {currentLine?.product_id ? (
              <div className="text-sm font-medium py-2 px-3 bg-muted/30 rounded">
                {currentLine?.product_name}
              </div>
            ) : (
              <SearchableSelect
                options={products.map(p => ({ value: p.id, label: `${p.name}${p.sku ? ` (${p.sku})` : ''}` }))}
                value={currentLine?.product_id || ''}
                onValueChange={(value) => {
                  const product = products.find(p => p.id === value);
                  onUpdateLine(lineIndex, 'product_id', value);
                  if (product) onUpdateLine(lineIndex, 'product_name', product.name);
                }}
                placeholder="Search product..."
                data-testid="dialog-select-product"
              />
            )}
          </div>
          
          <div className="space-y-2">
            <Label>Packaging / Brand</Label>
            <Select
              value={currentLine?.cobranding_id || 'none'}
              onValueChange={(value) => onUpdateLine(lineIndex, 'cobranding_id', value === 'none' ? '' : value)}
            >
              <SelectTrigger data-testid="dialog-select-packaging">
                <SelectValue placeholder="Standard (none)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Standard (none)</SelectItem>
                {cobrandings.map(cb => (
                  <SelectItem key={cb.id} value={cb.id}>{cb.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Customer Product Code *</Label>
            <Input
              value={supplierCode}
              onChange={(e) => setSupplierCode(e.target.value)}
              placeholder="e.g., CB-BLU-250G"
              className="font-mono"
              data-testid="input-new-supplier-code"
            />
            <p className="text-xs text-muted-foreground">
              This code will be saved and available in the dropdown for future orders.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); setSupplierCode(''); }}>Cancel</Button>
          <Button onClick={onSave} disabled={saving || !currentLine?.product_id} data-testid="btn-save-supplier-code">
            {saving ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
            ) : (
              <><Save className="w-4 h-4 mr-2" /> Save Code</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
